import { useEffect, useState } from 'react';

/**
 * True when the primary input can genuinely hover — a mouse or trackpad, not a finger.
 *
 * Why this is needed. Menus that open on `mouseenter` and close on `mouseleave` are broken on
 * touch: a tap synthesises `mouseover` but nothing ever synthesises `mouseleave`, so the menu
 * opens and then stays open. Worse, when the same element also toggles on `click`, the
 * synthesised `mouseover` opens it and the real `click` immediately closes it again — which is
 * why the nav bar needed two taps to reach a page and left its dropdown hanging open after
 * navigating (reported on iPad).
 *
 * The fix is to bind the hover handlers only where hover exists and rely on click/tap elsewhere.
 * `(hover: hover) and (pointer: fine)` is the standard test; both are required because some
 * touch devices report `hover: hover` for a stylus.
 *
 * Note this is deliberately about *input capability*, not viewport width — a touchscreen laptop
 * at 1440px still wants tap semantics, and `useDeviceSize` cannot tell you that.
 */
const QUERY = '(hover: hover) and (pointer: fine)';

export const useHoverCapable = (): boolean => {
  const [hoverCapable, setHoverCapable] = useState<boolean>(
    // Guard `matchMedia` for jsdom/SSR; assume hover so desktop behaviour is the default.
    () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(QUERY).matches : true)
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(QUERY);
    const onChange = (e: MediaQueryListEvent) => setHoverCapable(e.matches);
    setHoverCapable(mql.matches);
    // addEventListener on MediaQueryList is unsupported on older Safari, which needs addListener.
    if (mql.addEventListener) {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  return hoverCapable;
};

export default useHoverCapable;
